/*
 * <mtc-carousel-progress> — scrollbar-style progress control for a native
 * scroll-snap track. The markup is rendered in Liquid by the -mtc list sections;
 * this element binds to the .carousel__progress it finds inside itself.
 *
 * Attributes:
 *   for="<css selector>"     scroll container to control (searched within the
 *                            closest section; default: first [ref="scroller"]).
 *   autoplay                 enable timed advance (one card per tick).
 *   autoplay-interval="5"    seconds between ticks (default 5).
 */
(function () {
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var PAUSE_AFTER_MS = 600;

  class MtcCarouselProgress extends HTMLElement {
    connectedCallback() {
      this.scroller = this.#findScroller();
      if (!this.scroller) return;

      this.track = this.querySelector('.carousel__progress');
      this.thumb = this.track?.querySelector('.carousel__progress-thumb');
      if (!this.track || !this.thumb) return;

      this.#abort = new AbortController();
      var signal = this.#abort.signal;

      // Pause reasons are per connection. A disconnect mid-drag never runs onUp, so a
      // stale reason would survive and keep autoplay stopped forever on reconnect.
      // Seed the two we can read now; the observer supplies offscreen shortly after.
      this.#paused.clear();
      if (document.hidden) this.#paused.add('hidden');
      this.#paused.add('offscreen');

      this.scroller.addEventListener('scroll', this.#onScroll, { passive: true, signal });

      this.#resizeObserver = new ResizeObserver(this.#update);
      this.#resizeObserver.observe(this.scroller);

      this.#wireScrub(signal);
      this.#wireAutoplay(signal);

      this.#initRaf = requestAnimationFrame(this.#update);
    }

    disconnectedCallback() {
      this.#abort?.abort();
      cancelAnimationFrame(this.#initRaf);
      cancelAnimationFrame(this.#scrollRaf);
      this.#initRaf = 0;
      this.#scrollRaf = 0;
      this.#resizeObserver?.disconnect();
      this.#intersectionObserver?.disconnect();
      this.#resumeTimers.forEach(clearTimeout);
      this.#resumeTimers.clear();
      this.#stop();
    }

    /** @returns {HTMLElement|null} */
    #findScroller() {
      var root = this.closest('.section-resource-list, [id^="shopify-section-"]') || document;

      var explicit = this.getAttribute('for');
      if (explicit) return root.querySelector(explicit);

      // [ref="scroller"] is ambiguous: every product card image gallery is also a
      // <slideshow-slides ref="scroller">. The carousel TRACK is the one that is not
      // inside a product card. Return the first such scroller.
      var candidates = root.querySelectorAll('[ref="scroller"]');
      for (var i = 0; i < candidates.length; i++) {
        if (!candidates[i].closest('product-card, .product-card')) return candidates[i];
      }
      return null;
    }

    #onScroll = () => {
      // rAF-throttle scroll-driven updates
      if (this.#scrollRaf) return;
      this.#scrollRaf = requestAnimationFrame(() => {
        this.#scrollRaf = 0;
        this.#update();
      });
    };

    #update = () => {
      // A queued frame can still fire after a section re-render, and #update can
      // restart autoplay, so refuse to run on a detached element.
      if (!this.isConnected) return;
      var s = this.scroller;
      if (!s) return;

      var scrollable = s.scrollWidth - s.clientWidth;
      // No overflow (or hidden container) → hide the control and pause autoplay.
      if (scrollable <= 1 || s.clientWidth === 0) {
        this.hidden = true;
        this.#stop();
        return;
      }
      this.hidden = false;

      var trackWidth = this.track.clientWidth;
      var visibleRatio = s.clientWidth / s.scrollWidth;
      var thumbWidth = Math.max(24, visibleRatio * trackWidth);
      var posRatio = s.scrollLeft / scrollable;
      var x = posRatio * (trackWidth - thumbWidth);

      this.thumb.style.width = thumbWidth + 'px';
      this.thumb.style.transform = 'translateX(' + x + 'px)';
      this.track.setAttribute('aria-valuenow', String(Math.round(posRatio * 100)));

      if (this.#autoplayOn && !this.#timer) this.#start();
    };

    #wireScrub(signal) {
      var track = this.track;

      var scrollToClientX = (clientX) => {
        var rect = track.getBoundingClientRect();
        var thumbWidth = this.thumb.getBoundingClientRect().width;
        var half = thumbWidth / 2;
        var clamped = Math.max(rect.left + half, Math.min(clientX, rect.right - half));
        var pct = (clamped - rect.left - half) / (rect.width - thumbWidth || 1);
        var scrollable = this.scroller.scrollWidth - this.scroller.clientWidth;
        this.scroller.scrollTo({ left: pct * scrollable, behavior: 'smooth' });
      };

      track.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this.#suspend('drag');
        track.setPointerCapture(e.pointerId);
        scrollToClientX(e.clientX);
        var onMove = (ev) => scrollToClientX(ev.clientX);
        // A cancelled or browser-hijacked touch gesture fires pointercancel, not
        // pointerup, so unbind on every way the drag can end.
        var onUp = () => {
          track.removeEventListener('pointermove', onMove);
          track.removeEventListener('pointerup', onUp);
          track.removeEventListener('pointercancel', onUp);
          track.removeEventListener('lostpointercapture', onUp);
          this.#resume('drag');
        };
        track.addEventListener('pointermove', onMove);
        track.addEventListener('pointerup', onUp);
        track.addEventListener('pointercancel', onUp);
        track.addEventListener('lostpointercapture', onUp);
      }, { signal });

      track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var delta = (e.key === 'ArrowRight' ? 1 : -1) * this.scroller.clientWidth;
        this.scroller.scrollBy({ left: delta, behavior: 'smooth' });
      }, { signal });
    }

    #wireAutoplay(signal) {
      // Bail only when autoplay was never asked for. If reduced motion is what
      // disabled it, stay wired so clearing the preference takes effect.
      if (!this.hasAttribute('autoplay')) return;
      this.#autoplayOn = !REDUCED_MOTION.matches;

      var interval = parseFloat(this.getAttribute('autoplay-interval') || '5');
      this.#intervalMs = (isNaN(interval) ? 5 : interval) * 1000;

      // Hover pauses for a mouse or pen only. A touch fires pointerenter too, but iOS
      // does not reliably send the pointerleave once the touch turns into a page
      // scroll, which left autoplay stopped for good after scrolling past it.
      var onEnter = (reason) => (e) => {
        if (e.pointerType !== 'touch') this.#suspend(reason);
      };
      this.addEventListener('pointerenter', onEnter('pointer'), { signal });
      this.addEventListener('pointerleave', () => this.#resume('pointer'), { signal });
      this.scroller.addEventListener('pointerenter', onEnter('pointer-scroller'), { signal });
      this.scroller.addEventListener('pointerleave', () => this.#resume('pointer-scroller'), { signal });
      // A finger on the track holds autoplay until shortly after it lifts. Touch events,
      // not pointer events: a native pan fires pointercancel as soon as it takes over,
      // while touchend waits for the finger. Listened for on the document, so the
      // release counts wherever the gesture ends.
      // A new touch cancels the previous one's pending release, or it would resume
      // autoplay under this finger.
      this.scroller.addEventListener('touchstart', () => {
        clearTimeout(this.#resumeTimers.get('touch'));
        this.#resumeTimers.delete('touch');
        this.#suspend('touch');
      }, { passive: true, signal });
      var onTouchEnd = () => {
        if (this.#paused.has('touch')) this.#pauseBriefly('touch');
      };
      document.addEventListener('touchend', onTouchEnd, { passive: true, signal });
      document.addEventListener('touchcancel', onTouchEnd, { passive: true, signal });
      // iOS Safari can cut a page flick short and settle back on a snap track that is
      // scrolled by script mid-flick, so no tick runs while the page is scrolling.
      // Element scroll events do not reach window, so the track's own ticks don't count.
      // An offscreen carousel is already held, so it skips the timer churn.
      var onPageScroll = () => {
        if (!this.#paused.has('offscreen')) this.#pauseBriefly('page-scroll');
      };
      window.addEventListener('scroll', onPageScroll, { passive: true, signal });
      document.querySelector('.page-wrapper')?.addEventListener('scroll', onPageScroll, { passive: true, signal });
      this.scroller.addEventListener('focusin', () => this.#suspend('focus-scroller'), { signal });
      this.scroller.addEventListener('focusout', () => this.#resume('focus-scroller'), { signal });
      // The focusable track lives in this element, not in the scroller, so keyboard
      // scrubbing would otherwise run against autoplay.
      this.addEventListener('focusin', () => this.#suspend('focus'), { signal });
      this.addEventListener('focusout', () => this.#resume('focus'), { signal });
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.#suspend('hidden') : this.#resume('hidden');
      }, { signal });
      REDUCED_MOTION.addEventListener('change', () => {
        this.#autoplayOn = this.hasAttribute('autoplay') && !REDUCED_MOTION.matches;
        this.#autoplayOn ? this.#start() : this.#stop();
      }, { signal });

      // Only run while the carousel is on screen.
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          var inView = entries[0]?.isIntersecting ?? false;
          inView ? this.#resume('offscreen') : this.#suspend('offscreen');
        },
        { threshold: 0.2 }
      );
      this.#intersectionObserver.observe(this.scroller);
    }

    #start() {
      if (this.#timer || !this.#autoplayOn || this.#paused.size) return;
      this.#timer = setInterval(() => this.#tick(), this.#intervalMs);
    }

    #stop() {
      clearInterval(this.#timer);
      this.#timer = 0;
    }

    // Each cause holds its own key: hovering out must not resume while the control
    // still has focus, the tab is hidden, or the carousel is off screen.
    #suspend(reason) {
      this.#paused.add(reason);
      this.#stop();
    }

    #resume(reason) {
      this.#paused.delete(reason);
      this.#start();
    }

    // Holds autoplay for a reason and releases it after a quiet spell; a repeat call
    // restarts the wait, so a run of scroll events keeps it held until they stop.
    #pauseBriefly(reason) {
      this.#suspend(reason);
      clearTimeout(this.#resumeTimers.get(reason));
      this.#resumeTimers.set(reason, setTimeout(() => {
        this.#resumeTimers.delete(reason);
        this.#resume(reason);
      }, PAUSE_AFTER_MS));
    }

    #tick() {
      var s = this.scroller;
      var scrollable = s.scrollWidth - s.clientWidth;
      if (scrollable <= 1) return;
      if (s.scrollLeft >= scrollable - 2) {
        s.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        s.scrollBy({ left: this.#cardStep(), behavior: 'smooth' });
      }
    }

    /** @returns {number} distance to advance one card (slide width + gap). */
    #cardStep() {
      var slides = this.scroller.children;
      if (slides.length >= 2) {
        var step = slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
        if (step > 0) return step;
      }
      if (slides.length >= 1) return slides[0].getBoundingClientRect().width;
      return this.scroller.clientWidth;
    }

    #abort;
    #initRaf = 0;
    #resizeObserver;
    #intersectionObserver;
    #timer = 0;
    #scrollRaf = 0;
    #intervalMs = 5000;
    #autoplayOn = false;
    #paused = new Set();
    #resumeTimers = new Map();
  }

  if (!customElements.get('mtc-carousel-progress')) {
    customElements.define('mtc-carousel-progress', MtcCarouselProgress);
  }
})();
